import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { DeleteIcon, EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { Group } from '@/services/groupsManagement/groupsManagement.types';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import ManageGroupsFormContainer from '../../ManageGroupsFormContainer';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

enum status {
  Active = 'A',
  Blocked = 'B',
}

export function getColumns(
  onOpen: OnOpenProps,
  t: (key: string) => string,
  openConfirmation: OpenConfirmationProps,
  mutateAsync: (payload: { groupId: string; status: 'A' | 'B' | 'D' }) => Promise<unknown>,
): ColumnDef<Group>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ table, row }) => <div className="truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>,
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'groupInfo',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.groupInfo')} />,
      cell: ({ row }) => {
        const isActive = (String(row.getValue('status')).toUpperCase() as status) === status.Active;
        return (
          <div className="flex items-center space-x-3 min-w-0">
            <img
              src={row.original.groupPhoto.path}
              alt={row.original.groupPhoto.alt}
              className="w-8 h-8 rounded-full object-cover shrink-0"
            />

            <div className="min-w-0 flex-1">
              {isActive ? (
                <Link
                  to="/configuration-management/group-management/$sub-group"
                  params={{ 'sub-group': row.original.groupId }}
                  search={(previous) => ({
                    createdDate: previous.createdDate,
                    breadcrumbReturnTo: window.location.href,
                  })}
                >
                  <div className="font-medium text-caption text-link underline truncate">{row.original.groupInfo}</div>
                </Link>
              ) : (
                <div className="font-medium text-caption text-link underline truncate hover:cursor-not-allowed">
                  {row.original.groupInfo}
                </div>
              )}

              <div className="text-foreground-subtle font-medium text-caption truncate">
                {row.original.groupInfoKatakana}
              </div>
            </div>
          </div>
        );
      },

      enableHiding: false,
    },
    {
      accessorKey: 'totalSubGroup',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalSubGroups')} />
      ),
      cell: ({ row }) => <div>{row.getValue('totalSubGroup')}</div>,

      enableHiding: false,
    },
    {
      accessorKey: 'totalStores',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalStores')} />
      ),
      cell: ({ row }) => <div>{row.getValue('totalStores')}</div>,

      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        const isActive = (String(row.getValue('status')).toUpperCase() as status) === status.Active;
        return isActive ? (
          <div className="text-success">{t('common.page.table.active')}</div>
        ) : (
          <div className="text-destructive-text">{t('common.page.table.blocked')}</div>
        );
      },

      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap">{formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD')}</div>
      ),
      enableColumnFilter: true,
      enableHiding: false,
    },
    {
      accessorKey: 'actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.actions')} />,
      cell: ({ row }) => {
        const isActive = (String(row.getValue('status')).toUpperCase() as status) === status.Active;
        return (
          <div className="flex gap-4">
            {isActive && (
              <Can rule={{ allOf: [PERMS.group.manage.group, PERMS.group.get.detail] }}>
                <Button
                  variant="ghost"
                  className="p-0 m-0 "
                  onClick={() => {
                    onOpen({
                      title: 'Manage Groups',
                      okText: <CrumbTitle id="common.page.modal.submit" />,
                      size: 'lg',
                      modalTitle: 'configurationManagement.groupsManagement.manageGroups.title',
                      component: <ManageGroupsFormContainer id={row.original.groupId} />,
                    });
                  }}
                >
                  <EditIcon className="text-foreground-subtle size-4" />
                </Button>
              </Can>
            )}
            <Can rule={{ allOf: [PERMS.group.update.status] }}>
              <Button
                variant="ghost"
                className="p-0 m-0"
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirm'),
                    description: isActive ? t('common.page.modal.blockMessage') : t('common.page.modal.unblockMessage'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.submit'),
                    onAction: async () => {
                      await mutateAsync({
                        groupId: row.original.groupId,
                        status: isActive ? 'B' : 'A',
                      });
                    },
                    onCancel: () => {},
                    size: 'lg',
                    variant: 'warning',
                  });
                }}
              >
                {isActive ? (
                  <LockCloseIcon className="text-destructive-text size-4" />
                ) : (
                  <LockOpenIcon className="text-link-secondary size-4" />
                )}
              </Button>
            </Can>
            {isActive && (
              <Can rule={{ allOf: [PERMS.group.update.status] }}>
                <Button
                  variant="ghost"
                  className="p-0 m-0"
                  onClick={() => {
                    openConfirmation({
                      title: t('common.page.modal.confirm'),
                      description: t('storeManagement.basicStores.confirmationModal.deleteItem'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.submit'),
                      onAction: async () => {
                        await mutateAsync({
                          groupId: row.original.groupId,
                          status: 'D',
                        });
                      },
                      onCancel: () => {},
                      size: 'lg',
                      variant: 'destructive',
                    });
                  }}
                >
                  <DeleteIcon className="text-destructive-text size-4" />
                </Button>
              </Can>
            )}
          </div>
        );
      },
      size: 40,
    },
  ];
}
